import { PolfEventRankingEnum } from '@polf/shared/codegen/types'
import { useState } from 'react'

import type {
  GolfLeaderboardPlayerEntryFragment,
  LeaderboardUnionFieldsFragment,
} from '../../__generated__/hooks'
import GolfDetail from './GolfDetail'
import GolfRow from './GolfRow'
import Header from './Header'
import Hero from './Hero'
import type { CourseHoleSummary } from './MiniScorecard'
import TeamDetail from './TeamDetail'
import TeamRow from './TeamRow'
import Toggle from './Toggle'

type BestBallLb = Extract<
  LeaderboardUnionFieldsFragment,
  { __typename: 'BestBallLeaderboard' }
>
type BestBallTeam = BestBallLb['teams'][number]
type View = 'team' | 'individual'

/**
 * Best ball — both team and individual rankings live in the payload. The
 * Toggle swaps the entire list block, and the Hero tracks the active view
 * (team rank with "lag" copy when in team view, individual rank with
 * "spillere" copy in individual view). Default lands on team since that's
 * the format-native ranking.
 *
 * Both row variants are tappable: team rows open `TeamDetail`, player
 * rows open `GolfDetail`. Independent selection state per variant so
 * toggling views doesn't carry an orphaned selection across.
 */
const BestBallView = ({
  lb,
  youId,
  yourTeamId,
  ranking,
  courseHoles,
}: {
  lb: BestBallLb
  youId?: string
  yourTeamId?: string | null
  ranking?: PolfEventRankingEnum | null
  courseHoles: readonly CourseHoleSummary[]
}) => {
  const [view, setView] = useState<View>('team')
  const [selectedTeam, setSelectedTeam] = useState<BestBallTeam | null>(null)
  const [selectedPlayer, setSelectedPlayer] =
    useState<GolfLeaderboardPlayerEntryFragment | null>(null)
  const isNet = ranking === PolfEventRankingEnum.NetStrokes
  const teams = lb.teams ?? []
  const players = lb.players ?? []
  const yourTeam = yourTeamId
    ? teams.find((t) => t.team.id === yourTeamId) ?? null
    : null
  const you = youId ? players.find((p) => p.player.id === youId) ?? null : null

  return (
    <section className="pb-4">
      <Header
        controls={
          <Toggle
            options={[
              { value: 'team', label: 'Best ball' },
              { value: 'individual', label: 'Individuelt' },
            ]}
            value={view}
            onChange={setView}
          />
        }
      />

      {view === 'team' && yourTeam ? (
        <Hero
          yourRank={yourTeam.rank ?? null}
          yourPrimary={String(isNet ? yourTeam.score : yourTeam.points)}
          primarySuffix={isNet ? 'slag' : 'pts'}
          leaderGap={null}
          totalPlayers={teams.length}
          unit="lag"
        />
      ) : null}
      {view === 'individual' && you ? (
        <Hero
          yourRank={you.rank ?? null}
          yourPrimary={String(
            isNet ? you.strokesNet || you.strokes : you.points
          )}
          primarySuffix={isNet ? 'slag' : 'pts'}
          leaderGap={null}
          totalPlayers={players.length}
        />
      ) : null}

      <ul className="bg-paper mx-2 rounded-lg shadow-sm overflow-hidden">
        {view === 'team'
          ? teams.map((entry) => (
              <TeamRow
                key={entry.team.id}
                entry={entry}
                isYou={yourTeamId != null && entry.team.id === yourTeamId}
                isNet={isNet}
                onClick={() => setSelectedTeam(entry)}
              />
            ))
          : players.map((entry) => (
              <GolfRow
                key={entry.player.id}
                entry={entry}
                isYou={youId != null && entry.player.id === youId}
                isNet={isNet}
                onClick={() => setSelectedPlayer(entry)}
              />
            ))}
      </ul>

      <TeamDetail
        open={selectedTeam != null}
        onClose={() => setSelectedTeam(null)}
        courseHoles={courseHoles}
        data={
          selectedTeam
            ? {
                rank: selectedTeam.rank ?? null,
                teamName: selectedTeam.team.name ?? 'Uten navn',
                flightNumber: selectedTeam.team.flight?.flightNumber ?? null,
                members: selectedTeam.team.players ?? [],
                score: selectedTeam.score,
                strokes: selectedTeam.strokes,
                strokesNet: selectedTeam.strokesNet,
                points: selectedTeam.points,
                holesRegistred: selectedTeam.holesRegistred,
                scores: selectedTeam.scores,
              }
            : null
        }
      />
      <GolfDetail
        open={selectedPlayer != null}
        entry={selectedPlayer}
        isNet={isNet}
        courseHoles={courseHoles}
        onClose={() => setSelectedPlayer(null)}
      />
    </section>
  )
}

export default BestBallView
